// ─── Ambient Particles ──────────────────────────────────────────────────────
//
// Pooled, allocation-free atmospheric particle layer. One instance per scene;
// the particle pool is created once in the constructor and recycled forever —
// nothing is allocated in update() or draw(). Particles drift, fade, and wrap
// within the supplied screen-space bounds so the field always stays populated.
//
// Kinds (per zone mood):
//   pollen    — slow gold motes drifting up-right         (grass / day)
//   firefly   — pulsing gold-green glows, gentle wander    (grass / dusk-night)
//   dust      — pale sand specks, lazy fall                (cave / office)
//   ember     — warm gold/red sparks rising                (ops)
//   sparkle   — mystic-purple twinkles                     (creative)
//
// Colors are drawn from the PIXEL_STYLE master palette so the field reads as
// part of the world, not a filter. Soft additive glow only — never blurs the
// scene.

export type ParticleKind =
  | "pollen"
  | "firefly"
  | "dust"
  | "ember"
  | "sparkle";

export interface ParticleBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Particle {
  x: number; // screen-space px
  y: number;
  vx: number; // px per ms
  vy: number;
  size: number; // px
  phase: number; // twinkle phase
  pulse: number; // twinkle speed (rad/ms)
  life: number; // 0..1 base alpha
}

interface KindSpec {
  count: number;
  // velocity ranges (px / second — converted to /ms internally)
  vx: [number, number];
  vy: [number, number];
  size: [number, number];
  // r,g,b drawn from master palette
  color: [number, number, number];
  alpha: number; // peak alpha of the additive glow
  pulse: [number, number]; // twinkle speed range (rad/sec)
  glow: number; // soft radius multiplier (1 = size, 2 = soft halo)
}

// Tuned for the 800x600 logical canvas. count kept low — these run every frame.
const KIND_SPECS: Record<ParticleKind, KindSpec> = {
  // gold #c5a83a — slow upward drift, like sun-lit pollen
  pollen: {
    count: 28,
    vx: [-6, 10],
    vy: [-14, -4],
    size: [1, 2.5],
    color: [197, 168, 58],
    alpha: 0.5,
    pulse: [0.6, 1.6],
    glow: 2.2,
  },
  // gold-green blend — pulsing fireflies, gentle horizontal wander
  firefly: {
    count: 16,
    vx: [-12, 12],
    vy: [-8, 8],
    size: [1.5, 2.5],
    color: [188, 212, 160], // mint, warmed toward gold by the additive blend
    alpha: 0.75,
    pulse: [1.4, 3.0],
    glow: 3.0,
  },
  // sand #b89968 — soft falling dust motes for interiors / caves
  dust: {
    count: 22,
    vx: [-4, 4],
    vy: [4, 12],
    size: [1, 2],
    color: [184, 153, 104],
    alpha: 0.4,
    pulse: [0.4, 1.0],
    glow: 1.8,
  },
  // gold→alert-red embers rising (ops server-room heat)
  ember: {
    count: 20,
    vx: [-6, 6],
    vy: [-22, -8],
    size: [1, 2.5],
    color: [197, 138, 70], // amber midpoint of gold and alert red
    alpha: 0.6,
    pulse: [1.2, 2.6],
    glow: 2.6,
  },
  // mystic purple #a04ab8 — twinkling creative sparkles
  sparkle: {
    count: 18,
    vx: [-8, 8],
    vy: [-10, 6],
    size: [1, 2],
    color: [160, 74, 184],
    alpha: 0.7,
    pulse: [2.0, 4.0],
    glow: 2.4,
  },
};

// Deterministic-ish PRNG seeded per instance so fields differ between zones
// without per-frame Math.random churn during seeding.
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export class AmbientParticles {
  private kind: ParticleKind;
  private spec: KindSpec;
  private pool: Particle[];
  private color: string; // cached "r,g,b" prefix

  constructor(kind: ParticleKind) {
    this.kind = kind;
    this.spec = KIND_SPECS[kind];
    const [r, g, b] = this.spec.color;
    this.color = `${r}, ${g}, ${b}`;
    // Allocate the pool ONCE. Positions are seeded on first draw against the
    // real bounds; here we just create the objects.
    this.pool = new Array(this.spec.count);
    for (let i = 0; i < this.spec.count; i++) {
      this.pool[i] = {
        x: -1, // sentinel: "unseeded"
        y: 0,
        vx: 0,
        vy: 0,
        size: 0,
        phase: 0,
        pulse: 0,
        life: 0,
      };
    }
  }

  /** Re-randomize one particle in place (no allocation). */
  private seed(p: Particle, bounds: ParticleBounds): void {
    const s = this.spec;
    p.x = bounds.x + Math.random() * bounds.width;
    p.y = bounds.y + Math.random() * bounds.height;
    p.vx = rand(s.vx[0], s.vx[1]) / 1000; // px/ms
    p.vy = rand(s.vy[0], s.vy[1]) / 1000;
    p.size = rand(s.size[0], s.size[1]);
    p.phase = Math.random() * Math.PI * 2;
    p.pulse = rand(s.pulse[0], s.pulse[1]) / 1000; // rad/ms
    p.life = rand(0.6, 1) * s.alpha;
  }

  /**
   * Advance the field. dt is in ms. bounds is screen-space; particles that
   * drift outside wrap back to the opposite edge so the field stays full.
   * No allocation occurs here.
   */
  update(dt: number, bounds: ParticleBounds): void {
    const pad = 8;
    const left = bounds.x - pad;
    const right = bounds.x + bounds.width + pad;
    const top = bounds.y - pad;
    const bottom = bounds.y + bounds.height + pad;
    const w = bounds.width + pad * 2;
    const h = bounds.height + pad * 2;

    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (p.x < 0) {
        this.seed(p, bounds);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.phase += p.pulse * dt;

      // Toroidal wrap — cheaper than reseeding and keeps motion continuous.
      if (p.x < left) p.x += w;
      else if (p.x > right) p.x -= w;
      if (p.y < top) p.y += h;
      else if (p.y > bottom) p.y -= h;
    }
  }

  /**
   * Draw the field with additive ('lighter') compositing so motes read as
   * light, not paint. Soft radial gradients are cheap at this particle count.
   * The base scene is never touched.
   */
  draw(ctx: CanvasRenderingContext2D, bounds: ParticleBounds): void {
    const prevComposite = ctx.globalCompositeOperation;
    const prevAlpha = ctx.globalAlpha;
    ctx.globalCompositeOperation = "lighter";

    const glow = this.spec.glow;
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (p.x < 0) continue;
      // Twinkle: sine-modulated alpha, floored so motes never fully vanish.
      const tw = 0.55 + 0.45 * Math.sin(p.phase);
      const a = p.life * tw;
      if (a <= 0.02) continue;

      const r = p.size * glow;
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
      grad.addColorStop(0, `rgba(${this.color}, ${a.toFixed(3)})`);
      grad.addColorStop(0.5, `rgba(${this.color}, ${(a * 0.4).toFixed(3)})`);
      grad.addColorStop(1, `rgba(${this.color}, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = prevComposite;
    ctx.globalAlpha = prevAlpha;
  }

  /** Swap the kind without reallocating the pool (e.g. zone change). */
  setKind(kind: ParticleKind): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.spec = KIND_SPECS[kind];
    const [r, g, b] = this.spec.color;
    this.color = `${r}, ${g}, ${b}`;
    // Resize pool in place if needed (rare), reusing existing objects.
    if (this.pool.length !== this.spec.count) {
      const next: Particle[] = new Array(this.spec.count);
      for (let i = 0; i < this.spec.count; i++) {
        next[i] =
          this.pool[i] ??
          { x: -1, y: 0, vx: 0, vy: 0, size: 0, phase: 0, pulse: 0, life: 0 };
      }
      this.pool = next;
    }
    // Mark all for reseed so velocities/sizes match the new kind.
    for (let i = 0; i < this.pool.length; i++) this.pool[i].x = -1;
  }
}
